import React, {useContext, useEffect, useState} from "react";
import CONSTANTS from "src/utils/constants";
import {CalculationSourceType} from "src/models/common/CalculationSource";
import {ACTIONS} from "src/services/calculation-builder/JESettingsReducer";
import TPPolarisTableWithErrorHighlight from "src/components/shared/TPPolarisTableWithErrorHighlight";
import {useJournalLinesExcelBasedDownload} from "src/components/reports/downloadReports/DownloadJournalLinesReport";
import ServiceCollection from "src/services/ServiceCollection";
import JE7SegmentsValidator from "src/components/calculation-builder/je-settings/JE7SegmentsValidator";
import {ConfigureJournalsContext} from "src/components/calculation-builder/configure-journals/ConfigureJournalsView";
import {JESettingsContext} from "../je-settings/JESettingsContainer";
import {CalculationBuilderContext} from "../CalculationBuilderView";
import ReactTooltip from "react-tooltip";
import ArrayUtils from "src/utils/arrayUtils";
import {
    getColumnDefinitions,
    getJournalEntriesFilteringProperties,
    getJEFieldsTableColumns,
    getVisibleContentOptions,
    getDefaultCollectionPreferences
} from "src/components/calculation-builder/configure-journals/Utils";
import { PAGE_SIZE_OPTIONS } from "src/components/calculation-builder/configure-journals/Constants";
import {SpaceBetween, Toggle, Alert} from "@amzn/awsui-components-react/polaris";
import {JournalLine} from "src/models/calculation-builder/Journal";

export default function JournalEntriesGrid(props: {
    calculationSource: CalculationSourceType,
    recordNumber: string,
    services: ServiceCollection
}) {
    const {state, dispatch, services, calcBuilderState, userProfile} = useContext(ConfigureJournalsContext);
    const {calculationSource, recordNumber} = props;
    const {
        uploadJETemplateSuccess,
        generatingReviewJournalsStatus,
        journals,
        lastModifiedDate,
        lastModifiedUser,
        reviewJournals
    } = state;
    const [showReviewLines, setShowReviewLines] = useState(false);
    const [showInfoAlert, setShowInfoAlert] = useState(true);
    const [configJournalsRefresh, setConfigJournalsRefresh] = useState(Date.now());
    const [configJournalsResult, configJournalsLoading, configJournalsError] = services.journalsService.getJournalsV2(configJournalsRefresh,
        CONSTANTS.JOURNAL_LINE_TYPES.CONFIGURATION, recordNumber, CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION);
    const [reviewJournalsResult, reviewJournalsLoading, reviewJournalsError] = services.journalsService.getJournalsV2(true,
        CONSTANTS.JOURNAL_LINE_TYPES.REVIEW, recordNumber, CONSTANTS.CALCULATION_VERSIONS.DRAFT_VERSION);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [journals])

    // Reload config journals when upload succeeds
    useEffect(() => {
        if (uploadJETemplateSuccess) {
            setConfigJournalsRefresh(Date.now());
            setShowReviewLines(false);
            dispatch(ACTIONS.SET_VALIDATION_ERRORS.withPayload(null));
        }
    }, [uploadJETemplateSuccess]);

    // Reload review journals when generation succeeds
    useEffect(() => {
        if (generatingReviewJournalsStatus === CONSTANTS.GENERATE_REVIEW_JOURNAL_STATUS.SUCCEEDED || 
            generatingReviewJournalsStatus === CONSTANTS.GENERATE_REVIEW_JOURNAL_STATUS.VALIDATION_FAILED) {
            setShowReviewLines(true);
        }
    }, [generatingReviewJournalsStatus]);

    useEffect(() => {
        const configJournals = !ArrayUtils.isNullOrEmpty(configJournalsResult) ? configJournalsResult : [];

        const journals = showReviewLines
            ? reviewJournals    // Only review lines when toggle ON
            : configJournals;   // Only config lines when toggle OFF

        dispatch(ACTIONS.SET_JOURNALS.withPayload(journals));
    }, [configJournalsResult, reviewJournals, showReviewLines]);

    useEffect(() => {
        dispatch(ACTIONS.SET_REVIEW_JOURNALS.withPayload(!ArrayUtils.isNullOrEmpty(reviewJournalsResult) ? reviewJournalsResult : []));
    }, [reviewJournalsResult]);

    useEffect(() => {
        const error = configJournalsError || reviewJournalsError;
        if (error) {
            services.messageService.showErrorBanner(`Failed to get journals. ${error}`);
        }
    }, [configJournalsError, reviewJournalsError]);

    const journalLines: JournalLine[] = React.useMemo(() =>
            journals.flatMap(j => j.journalLines),
        [journals]
    );

    const isJournalLinesLoading = React.useMemo(() => {
        return (
            reviewJournalsLoading ||
            configJournalsLoading ||
            generatingReviewJournalsStatus === CONSTANTS.GENERATE_REVIEW_JOURNAL_STATUS.STARTED ||
            generatingReviewJournalsStatus === CONSTANTS.GENERATE_REVIEW_JOURNAL_STATUS.IN_PROGRESS
        );
    }, [reviewJournalsLoading, configJournalsLoading, generatingReviewJournalsStatus]);

    const hasJeTagData = React.useMemo(() =>
            journalLines.some(line =>
                line[CONSTANTS.JE_FIELDS.JE_TAG_ID.ACCESSOR as keyof JournalLine] ||
                line[CONSTANTS.JE_FIELDS.JE_TAG_DESCRIPTION.ACCESSOR as keyof JournalLine]
            ),
        [journalLines]);

    const fileName = React.useMemo(() => {
        const lineType = showReviewLines ? 'Review' : 'Configuration';
        return `${recordNumber}_${lineType}_Journal_Lines_${new Date().getTime()}.xlsx`;
    }, [recordNumber, showReviewLines]);

    return <CalculationBuilderContext.Provider value={{services, calcBuilderState, calcBuilderDispatch: () => {}, userProfile}}>
        <JESettingsContext.Provider value={{state, dispatch, services, calculationSource}}>
            <JE7SegmentsValidator/>
            <SpaceBetween direction="vertical" size="m">
                {showInfoAlert && !isJournalLinesLoading &&
                    <Alert type="info" dismissible onDismiss={() => {setShowInfoAlert(false)}} >
                        <SpaceBetween direction="horizontal" size="xs">
                            <div><b>Last updated by: </b><span>{showReviewLines ? 'System Generated' : lastModifiedUser}</span></div>
                            <div><b>Last updated on: </b><span>{lastModifiedDate}</span></div>
                        </SpaceBetween>
                    </Alert>
                }
                <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <Toggle
                        onChange={({detail}) => setShowReviewLines(detail.checked)}
                        checked={showReviewLines}
                    >
                        Show Review Lines
                    </Toggle>
                </div>
                <TPPolarisTableWithErrorHighlight
                    sortingColumn="calculationNumber"
                    sheetName="Journal lines"
                    fileName={fileName}
                    getDownloadFile={(sheetName, fileName) => useJournalLinesExcelBasedDownload(sheetName, fileName, hasJeTagData)}
                    tableEmptyState="There are no journal lines present for the draft version of CLI/ICRS number"
                    headerTitle={showReviewLines ? "Review journal lines" : "Configuration journal lines"}
                    columnDefinitions={getColumnDefinitions(calculationSource, hasJeTagData)}
                    tableItems={journalLines}
                    defaultPreferences={getDefaultCollectionPreferences(hasJeTagData)}
                    preferencesStorageKey="configureJournalsPreferencesStorageKey"
                    visibleContentOptions={getVisibleContentOptions(hasJeTagData)}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    filteringEmptyText="There are no journal lines present for the selected filter conditions."
                    filteringLoadingText="Loading journal lines for the filter conditions applied."
                    filteringProperties={getJournalEntriesFilteringProperties(calculationSource, hasJeTagData)}
                    loadingText="Loading journal lines."
                    resizableColumns={true}
                    visibleColumns={getJEFieldsTableColumns(hasJeTagData)}
                    isLoading={isJournalLinesLoading}
                />
            </SpaceBetween>
            <ReactTooltip place="top" type="error" effect="solid" event="mouseenter" eventOff="mouseleave"/>
        </JESettingsContext.Provider>
    </CalculationBuilderContext.Provider>
}